import React, { useCallback, useEffect, useRef, useState } from "react";

import Button from "veneer/Button";
import TextInput from "veneer/TextInput";
import Typography from "veneer/Typography";

import { ENV } from "cortex/ui/src/constant/app";
import { useAppDispatch, useFeatureFlag } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { FORGOT_PASSWORD } from "cortex/ui/src/redux-store/users/actions";
import Notification from "cortex/ui/src/ui/notification";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";

import BaseLoginView from "./BaseLoginView";

const { TRAFFICKING_URL } = ENV;

type FormValues = {
  email: string;
};

export default function ForgotPassword() {
  const dispatch = useAppDispatch();
  const isSsoLoginEnabled = useFeatureFlag("sso-login");

  const [form, setForm] = useState<FormValues>({ email: "" });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onForgotPassword = useCallback(
    (emailValue: string) => {
      return createPromiseApiAction(
        dispatch,
        FORGOT_PASSWORD,
        [emailValue],
        "",
      );
    },
    [dispatch],
  );

  const doForgotPassword = useCallback(() => {
    const { email } = form;
    if (!email) {
      setErrorMessage("Please enter your email address");
      setSubmitted(false);
      return Promise.resolve();
    }
    return onForgotPassword(email)
      .then(() => {
        if (isMountedRef.current) {
          setEmailSent(true);
        }
      })
      .catch((resp: unknown) => {
        if (isMountedRef.current) {
          const error = getErrorMessage(resp as Error | ResponseLike | null);
          setErrorMessage(
            error ||
              "An error occurred while attempting to reset the password. Please try again.",
          );
        }
      })
      .then(() => {
        if (isMountedRef.current) {
          setSubmitted(false);
        }
      });
  }, [form, onForgotPassword]);

  const submit = () => {
    if (submitted) {
      return;
    }
    setSubmitted(true);
    void doForgotPassword();
  };

  if (emailSent) {
    return (
      <BaseLoginView>
        {isSsoLoginEnabled ? (
          <Typography textAlign="center">
            If you have an active Fleet account, we'll send you an email with a
            link to reset your password. If your organization uses Vistar logins
            to access Fleet, reset your password in the{" "}
            <a href={TRAFFICKING_URL} rel="noopener" target="_blank">
              Vistar Platform
            </a>{" "}
            instead.
          </Typography>
        ) : (
          <Typography align="center">
            Password reset email is sent. Please check your inbox
          </Typography>
        )}
      </BaseLoginView>
    );
  }

  return (
    <BaseLoginView>
      <Typography align="center">Please enter your email address</Typography>
      <Notification error={errorMessage} />
      <TextInput
        fullWidth
        label="Email"
        onChange={(value) => setForm({ ...form, email: value ?? "" })}
        size="medium"
        value={form.email}
      />
      <Button disabled={submitted} fullWidth onClick={submit} size="large">
        Reset Password
      </Button>
    </BaseLoginView>
  );
}
